import { useEffect, useRef } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

const ThinkingDots = () => (
  <span
    aria-label="God is thinking"
    className="inline-flex items-center gap-1 align-middle"
  >
    <span className="inline-block h-1.5 w-1.5 rounded-full anim-dots" style={{ background: "hsl(var(--cg-cosmic))", animationDelay: "0ms" }} />
    <span className="inline-block h-1.5 w-1.5 rounded-full anim-dots" style={{ background: "hsl(var(--cg-cosmic))", animationDelay: "180ms" }} />
    <span className="inline-block h-1.5 w-1.5 rounded-full anim-dots" style={{ background: "hsl(var(--cg-cosmic))", animationDelay: "360ms" }} />
  </span>
);

export default function TranscriptPanel({ open, onToggle, transcript, callState }) {
  const scrollRef = useRef(null);

  // Auto-scroll to bottom when transcript grows
  useEffect(() => {
    if (!open || !scrollRef.current) return;
    scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [open, transcript, callState]);

  return (
    <div
      data-testid="transcript-panel"
      data-open={open}
      className="w-full"
      style={{ flex: open ? "1 1 50%" : "0 0 auto", minHeight: open ? 220 : "auto" }}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid="transcript-toggle"
        aria-expanded={open}
        className="mx-auto flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-foreground"
        style={{
          background: "hsla(0,0%,100%,0.6)",
          border: "1px solid hsla(38,25%,82%,0.7)",
        }}
      >
        {open ? "Hide transcript" : "Show transcript"}
        {open ? (
          <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.6} />
        ) : (
          <ChevronUp className="h-3.5 w-3.5" strokeWidth={1.6} />
        )}
      </button>

      {open ? (
        <div
          ref={scrollRef}
          data-testid="transcript-scroll"
          className="mt-3 max-h-[44vh] overflow-y-auto rounded-2xl p-4 cg-card"
        >
          {transcript.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Your conversation will appear here once you start speaking.
            </p>
          ) : (
            <ul className="space-y-3">
              {transcript.map((m) => {
                const isYou = m.who === "You";
                return (
                  <li
                    key={m.id}
                    data-testid={`transcript-row-${m.who.toLowerCase()}`}
                    className={`flex ${isYou ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className="max-w-[85%] rounded-2xl px-4 py-3 text-[14px] leading-6"
                      style={
                        isYou
                          ? {
                              background: "hsl(38 30% 92%)",
                              border: "1px solid hsla(38,25%,78%,0.7)",
                              borderTopRightRadius: 6,
                            }
                          : {
                              background:
                                "linear-gradient(180deg, hsla(230,55%,97%,0.95), hsla(214,55%,93%,0.9))",
                              border: "1px solid hsla(214,40%,82%,0.7)",
                              borderTopLeftRadius: 6,
                            }
                      }
                    >
                      <div
                        className="mb-1 text-[10px] uppercase tracking-[0.18em]"
                        style={{ color: isYou ? "hsl(30 9% 45%)" : "hsl(var(--cg-cosmic))" }}
                      >
                        <span className="sr-only">{isYou ? "You said:" : "God said:"} </span>
                        {m.who}
                      </div>
                      {m.text}
                    </div>
                  </li>
                );
              })}
              {callState === "processing" && transcript[transcript.length - 1]?.who === "You" ? (
                <li className="flex justify-end" data-testid="transcript-thinking">
                  <div className="rounded-2xl px-3 py-2" style={{
                    background: "hsla(214,55%,95%,0.85)",
                    border: "1px solid hsla(214,40%,82%,0.7)",
                  }}>
                    <ThinkingDots />
                  </div>
                </li>
              ) : null}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
